<template>
  <!-- Vue Router w tym miejscu wyświetla aktualny widok (np. LoginView) -->
  <router-view />
  <AppDialogModal ref="dialogComponent" />
</template>

<script setup>
import { ref, onMounted } from 'vue'
import AppDialogModal from '@/components/AppDialogModal.vue'
import { registerDialog } from '@/utils/dialog'

const dialogComponent = ref(null)

onMounted(() => {
  registerDialog(dialogComponent.value)
})
</script>

<style>
/* Reset i podstawa dla całej aplikacji */
body, button, input, select, textarea {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiasing;
  -moz-osx-font-smoothing: grayscale;
 background-color: #f8f9fa;
}
:root {
  /* Kolory bazowe na podstawie kulek z obrazka */
  --bs-primary: #3b99fc;  /* Jasnoniebieski */
  --bs-success: #00c875;  /* Żywy zielony */
  --bs-danger:  #ff5252;  /* Czerwony / Koralowy */
  --bs-warning: #ffd147;  /* Żółty */
  --bs-info:    #ffb04c;  /* Pomarańczowy */
  --easy-table-buttons-pagination-active-bg: #043873;
}

/* W razie gdyby biblioteka w danej wersji wymagała dokładniejszego nadpisania klasy: */
.vue3-easy-data-table .buttons-pagination .item.button.active {
  background-color: #043873 !important;
  border-color: #043873 !important;
  color: #ffffff !important;
}

/* Klasy dla przycisków wykorzystujące nowe zmienne Bootstrapa */
/* ==========================================================================
   1. STANDARDOWE WARIANTY PRZYCISKÓW (LIGHT MODE - DOMYŚLNE)
   ========================================================================== */

/* --- Solid Buttons --- */
.btn-primary {
  --bs-btn-color: #ffffff;
  --bs-btn-bg: #3b99fc;
  --bs-btn-border-color: #3b99fc;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #2982e0;
  --bs-btn-hover-border-color: #2982e0;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: #1e70cc;
  --bs-btn-active-border-color: #1e70cc;
  --bs-btn-focus-shadow-rgb: 59, 153, 252;
}

.btn-success {
  --bs-btn-color: #ffffff;
  --bs-btn-bg: #00c875;
  --bs-btn-border-color: #00c875;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #00ad65;
  --bs-btn-hover-border-color: #00ad65;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: #009456;
  --bs-btn-active-border-color: #009456;
  --bs-btn-focus-shadow-rgb: 0, 200, 117;
}

.btn-danger {
  --bs-btn-color: #ffffff;
  --bs-btn-bg: #ff5252;
  --bs-btn-border-color: #ff5252;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #e63e3e;
  --bs-btn-hover-border-color: #e63e3e;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: #cc2d2d;
  --bs-btn-active-border-color: #cc2d2d;
  --bs-btn-focus-shadow-rgb: 255, 82, 82;
}

.btn-warning {
  --bs-btn-color: #000000;
  --bs-btn-bg: #ffd147;
  --bs-btn-border-color: #ffd147;
  --bs-btn-hover-color: #000000;
  --bs-btn-hover-bg: #ebd131;
  --bs-btn-hover-border-color: #ebd131;
  --bs-btn-active-color: #000000;
  --bs-btn-active-bg: #d9be21;
  --bs-btn-active-border-color: #d9be21;
  --bs-btn-focus-shadow-rgb: 255, 209, 71;
}

.btn-info {
  --bs-btn-color: #ffffff;
  --bs-btn-bg: #ffb04c;
  --bs-btn-border-color: #ffb04c;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #e89938;
  --bs-btn-hover-border-color: #e89938;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: #d18326;
  --bs-btn-active-border-color: #d18326;
  --bs-btn-focus-shadow-rgb: 255, 176, 76;
}

.btn-primary-dark {
  --bs-btn-color: #ffffff;
  --bs-btn-bg: #043873;
  --bs-btn-border-color: #043873;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #032a57;
  --bs-btn-hover-border-color: #032a57;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: #021e3d;
  --bs-btn-active-border-color: #021e3d;
  --bs-btn-focus-shadow-rgb: 4, 56, 115;
}

.btn-primary-light {
  --bs-btn-color: #ffffff;
  --bs-btn-bg: #4f9cf9;
  --bs-btn-border-color: #4f9cf9;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #3786e3;
  --bs-btn-hover-border-color: #3786e3;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: #2270cd;
  --bs-btn-active-border-color: #2270cd;
  --bs-btn-focus-shadow-rgb: 79, 156, 249;
}

.btn-secondary-yellow {
  --bs-btn-color: #212529;
  --bs-btn-bg: #ffe492;
  --bs-btn-border-color: #ffe492;
  --bs-btn-hover-color: #212529;
  --bs-btn-hover-bg: #ebd180;
  --bs-btn-hover-border-color: #ebd180;
  --bs-btn-active-color: #212529;
  --bs-btn-active-bg: #d6be6f;
  --bs-btn-active-border-color: #d6be6f;
  --bs-btn-focus-shadow-rgb: 255, 228, 146;
}

.btn-secondary-soft {
  --bs-btn-color: #043873;
  --bs-btn-bg: #a7cefc;
  --bs-btn-border-color: #a7cefc;
  --bs-btn-hover-color: #043873;
  --bs-btn-hover-bg: #8ebbe8;
  --bs-btn-hover-border-color: #8ebbe8;
  --bs-btn-active-color: #043873;
  --bs-btn-active-bg: #77a8d4;
  --bs-btn-active-border-color: #77a8d4;
  --bs-btn-focus-shadow-rgb: 167, 206, 252;
}

.btn-secondary-dark {
  --bs-btn-color: #ffffff;
  --bs-btn-bg: #212529;
  --bs-btn-border-color: #212529;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #32383e;
  --bs-btn-hover-border-color: #32383e;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: #141619;
  --bs-btn-active-border-color: #141619;
  --bs-btn-focus-shadow-rgb: 33, 37, 41;
}


/* --- Outline Buttons (Light Mode) --- */
.btn-outline-primary {
  --bs-btn-color: #3b99fc;
  --bs-btn-border-color: #3b99fc;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #3b99fc;
  --bs-btn-hover-border-color: #3b99fc;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: #2982e0;
  --bs-btn-active-border-color: #2982e0;
  --bs-btn-focus-shadow-rgb: 59, 153, 252;
}

.btn-outline-success {
  --bs-btn-color: #00c875;
  --bs-btn-border-color: #00c875;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #00c875;
  --bs-btn-hover-border-color: #00c875;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: #00ad65;
  --bs-btn-active-border-color: #00ad65;
  --bs-btn-focus-shadow-rgb: 0, 200, 117;
}

.btn-outline-danger {
  --bs-btn-color: #ff5252;
  --bs-btn-border-color: #ff5252;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #ff5252;
  --bs-btn-hover-border-color: #ff5252;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: #e63e3e;
  --bs-btn-active-border-color: #e63e3e;
  --bs-btn-focus-shadow-rgb: 255, 82, 82;
}

.btn-outline-warning {
  --bs-btn-color: #d9a000;
  --bs-btn-border-color: #ffd147;
  --bs-btn-hover-color: #000000;
  --bs-btn-hover-bg: #ffd147;
  --bs-btn-hover-border-color: #ffd147;
  --bs-btn-active-color: #000000;
  --bs-btn-active-bg: #ebd131;
  --bs-btn-active-border-color: #ebd131;
  --bs-btn-focus-shadow-rgb: 255, 209, 71;
}

.btn-outline-info {
  --bs-btn-color: #ffb04c;
  --bs-btn-border-color: #ffb04c;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #ffb04c;
  --bs-btn-hover-border-color: #ffb04c;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: #e89938;
  --bs-btn-active-border-color: #e89938;
  --bs-btn-focus-shadow-rgb: 255, 176, 76;
}

.btn-outline-primary-dark {
  --bs-btn-color: #043873;
  --bs-btn-border-color: #043873;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #043873;
  --bs-btn-hover-border-color: #043873;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: #032a57;
  --bs-btn-active-border-color: #032a57;
  --bs-btn-focus-shadow-rgb: 4, 56, 115;
}

.btn-outline-primary-light {
  --bs-btn-color: #4f9cf9;
  --bs-btn-border-color: #4f9cf9;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #4f9cf9;
  --bs-btn-hover-border-color: #4f9cf9;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: #3786e3;
  --bs-btn-active-border-color: #3786e3;
  --bs-btn-focus-shadow-rgb: 79, 156, 249;
}

.btn-outline-secondary-yellow {
  --bs-btn-color: #b38b00;
  --bs-btn-border-color: #ffe492;
  --bs-btn-hover-color: #212529;
  --bs-btn-hover-bg: #ffe492;
  --bs-btn-hover-border-color: #ffe492;
  --bs-btn-active-color: #212529;
  --bs-btn-active-bg: #ebd180;
  --bs-btn-active-border-color: #ebd180;
  --bs-btn-focus-shadow-rgb: 255, 228, 146;
}

.btn-outline-secondary-soft {
  --bs-btn-color: #043873;
  --bs-btn-border-color: #a7cefc;
  --bs-btn-hover-color: #043873;
  --bs-btn-hover-bg: #a7cefc;
  --bs-btn-hover-border-color: #a7cefc;
  --bs-btn-active-color: #043873;
  --bs-btn-active-bg: #8ebbe8;
  --bs-btn-active-border-color: #8ebbe8;
  --bs-btn-focus-shadow-rgb: 167, 206, 252;
}

.btn-outline-secondary-dark {
  --bs-btn-color: #212529;
  --bs-btn-border-color: #212529;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #212529;
  --bs-btn-hover-border-color: #212529;
  --bs-btn-active-color: #ffffff;
  --bs-btn-active-bg: #32383e;
  --bs-btn-active-border-color: #32383e;
  --bs-btn-focus-shadow-rgb: 33, 37, 41;
}


/* ==========================================================================
   2. WARIANTY DLA DARK MODE ([data-bs-theme="dark"])
   Gwarantują wyższy kontrast na ciemnym tle oraz rozjaśnienie ciemnych odcieni.
   ========================================================================== */

[data-bs-theme="dark"] .btn-primary {
  --bs-btn-bg: #3b99fc;
  --bs-btn-border-color: #3b99fc;
  --bs-btn-hover-bg: #4fa5ff;
  --bs-btn-hover-border-color: #4fa5ff;
}

[data-bs-theme="dark"] .btn-success {
  --bs-btn-bg: #00d97e;
  --bs-btn-border-color: #00d97e;
  --bs-btn-hover-bg: #1ae08b;
  --bs-btn-hover-border-color: #1ae08b;
}

[data-bs-theme="dark"] .btn-danger {
  --bs-btn-bg: #ff6666;
  --bs-btn-border-color: #ff6666;
  --bs-btn-hover-bg: #ff7a7a;
  --bs-btn-hover-border-color: #ff7a7a;
}

[data-bs-theme="dark"] .btn-warning {
  --bs-btn-bg: #ffd55c;
  --bs-btn-border-color: #ffd55c;
  --bs-btn-hover-bg: #ffe085;
  --bs-btn-hover-border-color: #ffe085;
}

[data-bs-theme="dark"] .btn-info {
  --bs-btn-bg: #ffb85c;
  --bs-btn-border-color: #ffb85c;
  --bs-btn-hover-bg: #ffc475;
  --bs-btn-hover-border-color: #ffc475;
}

/* W Dark Mode granatowe "primary-dark" zmieniamy na jaśniejszy ton, by był widoczny */
[data-bs-theme="dark"] .btn-primary-dark {
  --bs-btn-color: #ffffff;
  --bs-btn-bg: #1a5294;
  --bs-btn-border-color: #215fa8;
  --bs-btn-hover-bg: #276dc0;
  --bs-btn-hover-border-color: #276dc0;
}

[data-bs-theme="dark"] .btn-primary-light {
  --bs-btn-bg: #5fa5fa;
  --bs-btn-border-color: #5fa5fa;
  --bs-btn-hover-bg: #77b3fa;
  --bs-btn-hover-border-color: #77b3fa;
}

[data-bs-theme="dark"] .btn-secondary-yellow {
  --bs-btn-color: #000000;
  --bs-btn-bg: #ffe8a3;
  --bs-btn-border-color: #ffe8a3;
  --bs-btn-hover-bg: #fff0c2;
  --bs-btn-hover-border-color: #fff0c2;
}

[data-bs-theme="dark"] .btn-secondary-soft {
  --bs-btn-color: #0a192f;
  --bs-btn-bg: #b0d4fd;
  --bs-btn-border-color: #b0d4fd;
  --bs-btn-hover-bg: #c5e0fe;
  --bs-btn-hover-border-color: #c5e0fe;
}

/* Szary przycisk secondary-dark rozjaśniamy lekko na bardzo ciemnym tle */
[data-bs-theme="dark"] .btn-secondary-dark {
  --bs-btn-color: #ffffff;
  --bs-btn-bg: #343a40;
  --bs-btn-border-color: #495057;
  --bs-btn-hover-bg: #495057;
  --bs-btn-hover-border-color: #6c757d;
}

/* --- Outline Buttons (Dark Mode) --- */
[data-bs-theme="dark"] .btn-outline-primary {
  --bs-btn-color: #60a5fa;
  --bs-btn-border-color: #60a5fa;
  --bs-btn-hover-bg: #3b99fc;
  --bs-btn-hover-border-color: #3b99fc;
}

[data-bs-theme="dark"] .btn-outline-success {
  --bs-btn-color: #34d399;
  --bs-btn-border-color: #34d399;
  --bs-btn-hover-bg: #00c875;
  --bs-btn-hover-border-color: #00c875;
}

[data-bs-theme="dark"] .btn-outline-danger {
  --bs-btn-color: #f87171;
  --bs-btn-border-color: #f87171;
  --bs-btn-hover-bg: #ff5252;
  --bs-btn-hover-border-color: #ff5252;
}

[data-bs-theme="dark"] .btn-outline-warning {
  --bs-btn-color: #fbbf24;
  --bs-btn-border-color: #fbbf24;
  --bs-btn-hover-color: #000000;
  --bs-btn-hover-bg: #ffd147;
  --bs-btn-hover-border-color: #ffd147;
}

[data-bs-theme="dark"] .btn-outline-info {
  --bs-btn-color: #fbbf24;
  --bs-btn-border-color: #ffb04c;
  --bs-btn-hover-bg: #ffb04c;
  --bs-btn-hover-border-color: #ffb04c;
}

[data-bs-theme="dark"] .btn-outline-primary-dark {
  --bs-btn-color: #93c5fd;
  --bs-btn-border-color: #60a5fa;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #1a5294;
  --bs-btn-hover-border-color: #215fa8;
}

[data-bs-theme="dark"] .btn-outline-primary-light {
  --bs-btn-color: #93c5fd;
  --bs-btn-border-color: #4f9cf9;
  --bs-btn-hover-bg: #4f9cf9;
  --bs-btn-hover-border-color: #4f9cf9;
}

[data-bs-theme="dark"] .btn-outline-secondary-yellow {
  --bs-btn-color: #ffe492;
  --bs-btn-border-color: #ffe492;
  --bs-btn-hover-color: #000000;
  --bs-btn-hover-bg: #ffe492;
  --bs-btn-hover-border-color: #ffe492;
}

[data-bs-theme="dark"] .btn-outline-secondary-soft {
  --bs-btn-color: #bfdbfe;
  --bs-btn-border-color: #a7cefc;
  --bs-btn-hover-color: #043873;
  --bs-btn-hover-bg: #a7cefc;
  --bs-btn-hover-border-color: #a7cefc;
}

[data-bs-theme="dark"] .btn-outline-secondary-dark {
  --bs-btn-color: #e2e8f0;
  --bs-btn-border-color: #64748b;
  --bs-btn-hover-color: #ffffff;
  --bs-btn-hover-bg: #475569;
  --bs-btn-hover-border-color: #64748b;
}

[data-bs-theme="dark"] .btn-outline-dark {
    --bs-btn-color: #f8f9fa;
    --bs-btn-border-color: #6c757d;
    --bs-btn-hover-color: #212529;
    --bs-btn-hover-bg: #f8f9fa;
    --bs-btn-hover-border-color: #f8f9fa;
    --bs-btn-focus-shadow-rgb: 248, 249, 250;
    --bs-btn-active-color: #212529;
    --bs-btn-active-bg: #e2e6ea;
    --bs-btn-active-border-color: #dae0e5;
    --bs-btn-active-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.5);
    --bs-btn-disabled-color: #6c757d;
    --bs-btn-disabled-bg: transparent;
    --bs-btn-disabled-border-color: #495057;
    --bs-gradient: none;
}

.btn, input, select, textarea, label {
  font-size: 13px !important;
}
/* Klasa stylująca całą tabelę vue-easy-data-table na 13px */
.table-font-13 {
  --easy-table-header-font-size: 13px;
  --easy-table-body-row-font-size: 13px;
  --easy-table-footer-font-size: 13px;
  font-size: 13px;
}

/* W wymuszających przypadkach nadpina też wewnętrzne elementy paginacji i komórek */
.table-font-13 .vue3-easy-data-table__main,
.table-font-13 .vue3-easy-data-table__footer,
.table-font-13 .vue3-easy-data-table__header th,
.table-font-13 .vue3-easy-data-table__body td {
  font-size: 13px !important;
}

.modal-header h5 {
  font-size: 16px;
}

.modal-header {
  padding:12px;
  background-color: var(--modal-header-bg);
}
@media (min-width: 768px) {
  .btn-responsive-auto {
    width: auto !important;
  }
}
@media (max-width: 767px) {
  .vue3-easy-data-table__footer {
    flex-wrap: wrap !important;
    height: auto !important;
    row-gap: 8px !important;
    justify-content: center !important;
    padding: 10px !important;
  }

  .vue3-easy-data-table__footer .paginations-per-page {
    width: 100% !important;
    justify-content: center !important;
  }

  .pagination__rows-per-page,
  .pagination__items-index {
    display: none !important;
  }
  .container-fluid {
    padding-left:0 !important;
    padding-right:0 !important;
  }
  main {
    max-width:100vw;
    overflow:auto;
  }
}


/*
paleta kolorow
*/
:root {
    --sidebar-bg: #a3caff;       
    --footer-bg: #a3caff;       
    --sidebar-text: #000000;
    --sidebar-active-bg: #0d3b66; 
    --sidebar-active-text: #ffffff;
    --main-bg: #f8f9fa;          
    --main-text: #212529;
    --border-color: #dee2e6;
    --bs-light-rgb: 248,249,250;
    --left-menu-bg: #a7cefc;
    --left-menu-selected-button: #043873;
    --left-menu-color: #043873;
    --left-menu-color-active: white;
    --modal-header-bg: #a7cefc;
    --nav-link-color: #6c757d;
    --nav-link-hover-color: #212529;
    --nav-link-hover-border-color: #dee2e6;
    --nav-link-active-color: #0d6efd;
    --nav-link-active-border-color: #0d6efd;
    --drop-zone-bg: #f8fafc;
    --drop-zone-border: #cbd5e1;
    --drop-zone-text: #64748b;
    --drop-zone-hover-bg: #f0f7ff;
    --drop-zone-hover-border: #0d6efd;
    /* Switch */
    --switch-bg: #cbd5e1;
    --switch-handle-bg: #ffffff;
    --switch-active-bg: #10b981;

    /* Komórki siatki (Portal cells) */
    --cell-hover-bg: #f1f5f9;
    --cell-selected-bg: #e0f2fe;
    --cell-selected-border: #0284c7;
    --cell-disabled-bg: #f8fafc;

    /* Pływający pasek (Toolbar) */
    --toolbar-bg: #ffffff;
    --toolbar-border: #e2e8f0;
    --toolbar-shadow: rgba(0, 0, 0, 0.1);

    /* Tabela */
    --table-border-color: #dee2e6;

    --header-background-color: white;
      --login-bg-card: rgba(240, 240, 240, 0.7);
  --login-card-border: rgba(0, 0, 0, 0.08);
  --login-input-bg: #ffffff;
  --login-input-border: #dee2e6;
  --login-input-text: #212529;
  --login-input-focus-border: #86b7fe;
  --login-btn-hover-icon: #212529;
}

/* Wariant dla Dark Mode */
[data-bs-theme="dark"] {
    --sidebar-bg: #1e293b;     
    --footer-bg: #202020;  
    --sidebar-text: #cbd5e1;
    --sidebar-active-bg: #0f172a; 
    --sidebar-active-text: #38bdf8;
    --main-bg: #0f172a;          
    --main-text: #f8fafc;
    --border-color: #334155;
    --bs-light-rgb: 28,29,20;
    --left-menu-bg: #043873;
    --left-menu-selected-button: #a7cefc;
    --left-menu-color: white;
    --left-menu-color-active: #043873;
    --modal-title: white;
    --modal-header-bg: #043873;
    --nav-link-color: #94a3b8;               /* Stonowany szary/niebieski, czytelny na ciemnym */
  --nav-link-hover-color: #f1f5f9;         /* Prawie biały przy najechaniu */
  --nav-link-hover-border-color: #475569;  /* Delikatny, widoczny obrys hovera */
  --nav-link-active-color: #38bdf8;        /* Jasnoniebieski (akcent), idealny pod dark mode */
  --nav-link-active-border-color: #38bdf8; /* Krawędźtywnego linku spójna z akcentem */
  --drop-zone-bg: #1e293b;               /* Ciemne tło obszaru */
    --drop-zone-border: #475569;           /* Widoczna, stonowana ramka */
    --drop-zone-text: #94a3b8;             /* Jasnoszary tekst */
    --drop-zone-hover-bg: #0f172a;         /* Głębszy odcień przy najechaniu/przeciąganiu */
    --drop-zone-hover-border: #38bdf8;     /* Jasnon*/
    /* Switch */
    --switch-bg: #475569;
    --switch-handle-bg: #f8fafc;
    --switch-active-bg: #059669;

    /* Komórki siatki (Portal cells) */
    --cell-hover-bg: #334155;
    --cell-selected-bg: #0369a1;
    --cell-selected-border: #38bdf8;
    --cell-disabled-bg: #0f172a;

    /* Pływający pasek (Toolbar) */
    --toolbar-bg: #1e293b;
    --toolbar-border: #334155;
    --toolbar-shadow: rgba(0, 0, 0, 0.5);

    /* Tabela */
    --table-border-color: #334155;

        --header-background-color: #202020;
    --bs-dark-rgb: white;

    --easy-table-message-font-color: #eee;

  
  /* Dark Mode - Login Styles */
  --login-bg-card: rgba(33, 37, 41, 0.85);
  --login-card-border: rgba(255, 255, 255, 0.1);
  --login-input-bg: #2b3035;
  --login-input-border: #495057;
  --login-input-text: #f8f9fa;
  --login-input-focus-border: #0d6efd;
  --login-btn-hover-icon: #ffffff;
  --bs-white-rgb: rgb(255,255,255);
}

[data-bs-theme="dark"] .vue3-easy-data-table {
  --easy-table-border: 1px solid #334155;
  --easy-table-row-border: 1px solid #334155;
  
  --easy-table-header-background-color: #0f172a;
  --easy-table-header-font-color: #f8fafc;
  
  --easy-table-body-row-background-color: #1e293b;
  --easy-table-body-row-font-color: #cbd5e1;
  --easy-table-body-row-hover-background-color: #334155;
  --easy-table-body-row-hover-font-color: #ffffff;
  
  --easy-table-footer-background-color: #0f172a;
  --easy-table-footer-font-color: #cbd5e1;
  
  --easy-table-scrollbar-track-color: #0f172a;
  --easy-table-scrollbar-color: #334155;
  --bs-white-rgb: rgb(0,0,0);
    --bs-table-bg: #0f172a;

}

/* 1. Wyłączamy domyślny backdrop Bootstrapa (żeby nie nachodził) */
.modal-backdrop.show {
    opacity: 0 !important;
}

/* 2. Tworzymy własne tło z filtrem blur na elemencie .modal */
.modal.show::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    will-change: backdrop-filter;
    transform: translateZ(0);
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.4); /* Przyciemnienie tła */
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);   /* Dla Safari / WebKit */
    z-index: -1;                           /* Zawsze pod oknem modala */
}

/* 3. Upewniamy się, że treść modala jest ostra i ma tło */
.modal-content {
    position: relative;
    z-index: 1;
    background-color: #ffffff;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

[data-bs-theme="dark"] .modal-content {
  background-color:#222;
}

</style>